import { X } from 'lucide-react';
import { type ChangeEvent, type ReactElement, useCallback, useState } from 'react';

import { Input, Label } from '@crossnokaye/ux-common';

import { Button } from '@/ux-common/components/button/button';

import type { NumberRangeFilterProps } from '../types';

/**
 * Number range filter with equals, greaterThan, and lessThan inputs.
 * Matches the old data-table filter UI.
 */
export const NumberRangeFilter = ({
  columnId,
  value,
  onChange,
  onClear,
  isActive,
  onClose,
}: NumberRangeFilterProps & { isActive: boolean; onClose: () => void }): ReactElement => {
  // Local state for inputs
  const [equals, setEquals] = useState<string>(value.equals?.toString() ?? '');
  const [greaterThan, setGreaterThan] = useState<string>(value.greaterThan?.toString() ?? '');
  const [lessThan, setLessThan] = useState<string>(value.lessThan?.toString() ?? '');

  const handleNumberInput = (inputValue: string): number | undefined => {
    if (inputValue === '') return undefined;
    const num = parseFloat(inputValue);
    return isNaN(num) ? undefined : num;
  };

  const updateFilter = useCallback(
    (field: 'equals' | 'greaterThan' | 'lessThan', inputValue: string) => {
      const newValue = { ...value };
      const numValue = handleNumberInput(inputValue);

      if (numValue === undefined) {
        delete newValue[field];
      } else {
        newValue[field] = numValue;
      }

      // If all fields are empty, clear the filter completely
      if (Object.keys(newValue).length === 0) {
        onClear();
      } else {
        onChange(newValue);
      }
    },
    [value, onChange, onClear]
  );

  const handleEqualsChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setEquals(newValue);
      updateFilter('equals', newValue);
    },
    [updateFilter]
  );

  const handleGreaterThanChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setGreaterThan(newValue);
      updateFilter('greaterThan', newValue);
    },
    [updateFilter]
  );

  const handleLessThanChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setLessThan(newValue);
      updateFilter('lessThan', newValue);
    },
    [updateFilter]
  );

  const clearField = useCallback(
    (field: 'equals' | 'greaterThan' | 'lessThan', e: React.MouseEvent) => {
      e.stopPropagation();
      if (field === 'equals') setEquals('');
      if (field === 'greaterThan') setGreaterThan('');
      if (field === 'lessThan') setLessThan('');
      updateFilter(field, '');
    },
    [updateFilter]
  );

  return (
    <>
      <div className="p-4 space-y-4">
        <div className="space-y-3">
          <div>
            <Label htmlFor={`equals-${columnId}`} className="text-xs text-foreground/70">
              Equals
            </Label>
            <div className="relative mt-1">
              <Input
                id={`equals-${columnId}`}
                type="number"
                placeholder="Enter value"
                value={equals}
                onChange={handleEqualsChange}
                className="pr-8"
              />
              {equals && (
                <X
                  className="glyph glyph-sm absolute right-2 top-1/2 transform -translate-y-1/2 cursor-pointer text-foreground/50 hover:text-foreground"
                  onClick={(e) => clearField('equals', e)}
                />
              )}
            </div>
          </div>

          <div>
            <Label htmlFor={`greater-than-${columnId}`} className="text-xs text-foreground/70">
              Greater than
            </Label>
            <div className="relative mt-1">
              <Input
                id={`greater-than-${columnId}`}
                type="number"
                placeholder="Enter minimum value"
                value={greaterThan}
                onChange={handleGreaterThanChange}
                className="pr-8"
              />
              {greaterThan && (
                <X
                  className="glyph glyph-sm absolute right-2 top-1/2 transform -translate-y-1/2 cursor-pointer text-foreground/50 hover:text-foreground"
                  onClick={(e) => clearField('greaterThan', e)}
                />
              )}
            </div>
          </div>

          <div>
            <Label htmlFor={`less-than-${columnId}`} className="text-xs text-foreground/70">
              Less than
            </Label>
            <div className="relative mt-1">
              <Input
                id={`less-than-${columnId}`}
                type="number"
                placeholder="Enter maximum value"
                value={lessThan}
                onChange={handleLessThanChange}
                className="pr-8"
              />
              {lessThan && (
                <X
                  className="glyph glyph-sm absolute right-2 top-1/2 transform -translate-y-1/2 cursor-pointer text-foreground/50 hover:text-foreground"
                  onClick={(e) => clearField('lessThan', e)}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer with Clear/Close buttons */}
      <div className="flex justify-end p-3 border-t border-border">
        {isActive && (
          <Button
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              setEquals('');
              setGreaterThan('');
              setLessThan('');
              onClear();
            }}
            className="btn btn-tier3 btn-sm mr-2 font-semibold"
          >
            Clear
          </Button>
        )}
        <Button onClick={onClose} className="btn btn-tier2 btn-sm font-semibold">
          Close
        </Button>
      </div>
    </>
  );
};

NumberRangeFilter.displayName = 'NumberRangeFilter';
